import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  CheckCircle2, 
  Bot, 
  Brain, 
  LineChart, 
  MessageSquare,
  Eye,
  Workflow,
  Sparkles,
  Shield,
  Clock,
  DollarSign,
  Users,
  Building2,
  ShoppingCart,
  Factory,
  Stethoscope,
  Truck,
  Award,
  Zap,
  ChevronDown,
  ChevronUp,
  ArrowDownRight,
  ArrowUpRight
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  FadeInUp,
  StaggerContainer,
  StaggerItem,
  AnimatedCounter,
  FloatingElement,
  AnimatedBlobs,
  InteractiveCard,
  WaveDivider
} from '../components/AnimationComponents';
import { AISolutionsIndustrySection } from '../components/IndustrySections';

const AI_IMAGE = "https://images.pexels.com/photos/17485657/pexels-photo-17485657.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Hero Section with Animations
const HeroSection = () => (
  <section className="relative min-h-[85vh] flex items-center bg-[#0A2540] overflow-hidden" data-testid="ai-hero-section">
    <div className="absolute inset-0 opacity-20">
      <img src={AI_IMAGE} alt="" className="w-full h-full object-cover" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540] via-[#0A2540]/95 to-[#0A2540]/80" />
    <AnimatedBlobs />
    
    <div className="container-custom relative z-10 py-20">
      <div className="max-w-3xl">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 bg-[#2563EB]/20 border border-[#2563EB]/30 rounded-full px-4 py-2 mb-8"
        >
          <Sparkles className="w-4 h-4 text-[#2563EB]" />
          <span className="text-sm text-white/90">AI Solutions Designed for SMBs</span>
        </motion.div>
        
        <motion.h1 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="font-outfit text-4xl md:text-6xl font-bold text-white mb-6 leading-[1.1]"
        >
          Build{' '}
          <span className="text-[#2563EB]">ROI-Focused AI Use Cases</span>{' '}
          for Your Business
        </motion.h1>
        
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl leading-relaxed"
        >
          Not sure where AI actually fits in your business? Our AI readiness assessment helps you spot the gaps, find the real ROI, and figure out exactly where automation makes sense, before you invest in the wrong tool.
        </motion.p>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link to="/contact">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
              <Button 
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg shadow-[#2563EB]/25"
                data-testid="ai-hero-cta"
              >
                Book an AI Readiness Assessment
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </Link>
          <Link to="/case-studies">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
              <Button 
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 hover:text-white rounded-full px-8 py-6 text-lg font-medium"
              >
                See Client Results
              </Button>
            </motion.div>
          </Link>
        </motion.div>
      </div>
    </div>
  </section>
);

// Before You Buy Any Tool: AI Readiness perspective section (NEW)
const AIReadinessSection = () => (
  <section className="section-padding bg-gradient-to-b from-white via-slate-50 to-white" data-testid="ai-readiness-section">
    <div className="container-custom">
      <div className="max-w-4xl mx-auto">
        <FadeInUp className="text-center mb-10">
          <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">Start With Strategy, Not Software</span>
          <h2 className="font-outfit text-3xl md:text-5xl font-bold text-[#0A2540] mt-4 mb-4 leading-tight">
            Before You Buy Another AI Tool.
          </h2>
        </FadeInUp>

        <FadeInUp delay={0.15}>
          <div className="relative bg-white rounded-3xl border border-slate-100 shadow-xl p-8 md:p-12">
            {/* Left accent bar */}
            <div className="absolute top-8 bottom-8 left-0 w-1 bg-gradient-to-b from-[#2563EB] to-[#1D4ED8] rounded-r-full" />

            <div className="space-y-5 text-slate-700 text-lg leading-relaxed">
              <p>
                Too many business owners end up juggling a handful of AI tools without ever stopping to ask if it&rsquo;s the right approach, or if it&rsquo;s even paying off. A chatbot here, an automation app there, and pretty soon you&rsquo;ve got a messy patchwork of tools that don&rsquo;t talk to each other and a budget that&rsquo;s quietly draining away.
              </p>
              <p className="text-[#0A2540] font-medium">
                A smarter approach starts before you buy anything.
              </p>
              <p>
                Figure out where AI can genuinely make a difference in your business, rank those opportunities by what&rsquo;ll actually move the needle, and then pick tools that fit. Not the other way around.
              </p>
            </div>

            {/* 3-step mini flow */}
            <div className="mt-10 pt-8 border-t border-slate-100 grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { step: '01', title: 'Spot the Gaps', description: 'Identify where AI can genuinely make a difference in your business.', icon: Eye },
                { step: '02', title: 'Rank by Impact', description: 'Prioritize the opportunities that will actually move the needle.', icon: LineChart },
                { step: '03', title: 'Pick the Right Tools', description: 'Choose tools that fit the plan, not the other way around.', icon: Zap }
              ].map((s, i) => (
                <div key={i} className="flex gap-4" data-testid={`readiness-step-${i}`}>
                  <div className="flex-shrink-0">
                    <div className="w-11 h-11 bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] rounded-xl flex items-center justify-center">
                      <s.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-xs font-semibold text-slate-400 mt-2 tracking-wider">{s.step}</div>
                  </div>
                  <div>
                    <h3 className="font-outfit text-lg font-semibold text-[#0A2540] mb-1 leading-tight">{s.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{s.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 text-center">
              <Link to="/contact">
                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} className="inline-block">
                  <Button
                    className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-full px-8 py-6 text-base font-medium"
                    data-testid="ai-readiness-cta"
                  >
                    Book an AI Readiness Assessment
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </motion.div>
              </Link>
            </div>
          </div>
        </FadeInUp>
      </div>
    </div>
  </section>
);

// Animated Stats Bar
const StatsBar = () => (
  <section className="bg-white py-10 border-b border-slate-100" data-testid="stats-bar">
    <div className="container-custom">
      <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-6" staggerDelay={0.1}>
        {[
          { value: 25, suffix: "+", label: "Small Businesses Using AI", icon: Building2, color: "text-purple-500" },
          { value: 37, suffix: "%", label: "Avg Cost Reduction", icon: LineChart, color: "text-green-500" },
          { value: 40, suffix: "%", label: "Timeline Reduction", icon: Clock, color: "text-blue-500" },
          { value: 15, suffix: "+ hrs", label: "Saved per Week", icon: DollarSign, color: "text-orange-500" }
        ].map((stat, index) => (
          <StaggerItem key={index}>
            <motion.div
              className="flex items-center gap-4 group bg-slate-50 border border-slate-100 rounded-2xl p-5 h-full"
              whileHover={{ y: -3, borderColor: '#2563EB' }}
              data-testid={`stat-${index}`}
            >
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-bold text-[#0A2540] leading-tight">
                  {stat.prefix}<AnimatedCounter end={stat.value} suffix={stat.suffix} duration={2.5} />
                </div>
                <div className="text-sm text-slate-500 mt-1">{stat.label}</div>
              </div>
            </motion.div>
          </StaggerItem>
        ))}
      </StaggerContainer>
    </div>
  </section>
);

// Before/After Comparison
const BeforeAfterSection = () => {
  const [showAfter, setShowAfter] = useState(false);

  return (
    <section className="section-padding bg-slate-50" data-testid="before-after-section">
      <div className="container-custom">
        <FadeInUp className="text-center mb-12">
          <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">Transformation</span>
          <h2 className="font-outfit text-3xl md:text-4xl font-bold text-[#0A2540] mt-4 mb-4">
            See the AI Impact
          </h2>
          
          {/* Toggle */}
          <div className="inline-flex items-center gap-4 bg-white p-2 rounded-full shadow-lg mt-6">
            <button
              onClick={() => setShowAfter(false)}
              className={`px-6 py-3 rounded-full font-medium transition-all flex items-center gap-2 ${
                !showAfter ? 'bg-red-100 text-red-700' : 'text-slate-500'
              }`}
            >
              <ArrowDownRight className="w-4 h-4" />
              Before AI
            </button>
            <button
              onClick={() => setShowAfter(true)}
              className={`px-6 py-3 rounded-full font-medium transition-all flex items-center gap-2 ${
                showAfter ? 'bg-green-100 text-green-700' : 'text-slate-500'
              }`}
            >
              <ArrowUpRight className="w-4 h-4" />
              After AI
            </button>
          </div>
        </FadeInUp>

        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={showAfter ? 'after' : 'before'}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-6"
            >
              {(showAfter ? [
                { label: "Processing Time", value: "Minutes", icon: Clock, color: "bg-green-500" },
                { label: "Error Rate", value: "Near Zero", icon: Shield, color: "bg-green-500" },
                { label: "Cost Per Task", value: "-60%", icon: DollarSign, color: "bg-green-500" }
              ] : [
                { label: "Processing Time", value: "Hours/Days", icon: Clock, color: "bg-red-500" },
                { label: "Error Rate", value: "5-15%", icon: Shield, color: "bg-red-500" },
                { label: "Cost Per Task", value: "High", icon: DollarSign, color: "bg-red-500" }
              ]).map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-2xl border border-slate-200 text-center"
                >
                  <div className={`w-14 h-14 ${item.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-sm text-slate-500 mb-2">{item.label}</div>
                  <div className="text-2xl font-bold text-[#0A2540]">{item.value}</div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

// AI Solutions Grid
const SolutionsGrid = () => (
  <section className="section-padding" data-testid="solutions-grid">
    <div className="container-custom">
      <FadeInUp className="text-center mb-16">
        <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">Our AI Solutions</span>
        <h2 className="font-outfit text-3xl md:text-4xl font-bold text-[#0A2540] mt-4 mb-4">
          AI Tools That Actually Work for SMBs
        </h2>
        <p className="text-slate-600 max-w-xl mx-auto">
          No complex enterprise setups. Just practical AI solutions that deliver results from day one.
        </p>
      </FadeInUp>
      
      <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" staggerDelay={0.1}>
        {[
          { icon: MessageSquare, title: "AI Chatbots & Support", description: "24/7 customer support that reduces tickets by 60%", benefits: ["Multi-language", "CRM integration", "Human handoff"], color: "bg-blue-500" },
          { icon: LineChart, title: "Predictive Analytics", description: "Forecast demand and make data-driven decisions", benefits: ["Sales forecasting", "Inventory optimization", "Risk assessment"], color: "bg-purple-500" },
          { icon: Brain, title: "Agentic AI Systems", description: "Autonomous AI agents handling complex workflows", benefits: ["Task automation", "Decision making", "Process orchestration"], color: "bg-green-500" },
          { icon: Bot, title: "LLM Solutions", description: "Custom language models for your industry", benefits: ["Document processing", "Content generation", "Knowledge bases"], color: "bg-orange-500" },
          { icon: Workflow, title: "Process Automation", description: "End-to-end automation of repetitive tasks", benefits: ["Invoice processing", "Email automation", "Data migration"], color: "bg-pink-500" },
          { icon: Eye, title: "Computer Vision", description: "Visual inspection and image-based automation", benefits: ["Quality inspection", "Inventory counting", "Document scanning"], color: "bg-cyan-500" }
        ].map((solution, index) => (
          <StaggerItem key={index}>
            <InteractiveCard>
              <motion.div 
                className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:border-[#2563EB]/20 transition-all h-full group"
                data-testid={`ai-solution-${index}`}
              >
                <motion.div 
                  className={`w-16 h-16 ${solution.color} rounded-2xl flex items-center justify-center mb-6`}
                  whileHover={{ scale: 1.1, rotate: 10 }}
                >
                  <solution.icon className="w-8 h-8 text-white" />
                </motion.div>
                <h3 className="font-outfit text-xl font-semibold text-[#0A2540] mb-3">{solution.title}</h3>
                <p className="text-slate-600 mb-6">{solution.description}</p>
                <ul className="space-y-2">
                  {solution.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-500">
                      <CheckCircle2 className="w-4 h-4 text-[#2563EB]" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </InteractiveCard>
          </StaggerItem>
        ))}
      </StaggerContainer>
    </div>
  </section>
);

// Interactive Process Timeline
const ProcessTimeline = () => {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    { week: "Week 1-2", title: "Discovery", description: "We analyze your business processes and identify AI opportunities.", details: ["Stakeholder interviews", "Process mapping", "Data audit", "Opportunity scoring"] },
    { week: "Week 3-4", title: "Design", description: "Custom AI solutions designed for your specific needs.", details: ["Solution architecture", "Integration planning", "ROI projections", "Timeline creation"] },
    { week: "Week 5-6", title: "Build", description: "Development and testing with your team's feedback.", details: ["Agile development", "Weekly demos", "User testing", "Quality assurance"] },
    { week: "Week 7-8", title: "Launch", description: "Go live with full training and ongoing support.", details: ["Deployment", "Team training", "Documentation", "24/7 support"] }
  ];

  return (
    <section className="section-padding bg-[#0A2540] relative overflow-hidden" data-testid="process-timeline">
      <AnimatedBlobs />
      <div className="container-custom relative z-10">
        <FadeInUp className="text-center mb-16">
          <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">How It Works</span>
          <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mt-4 mb-4">
            From Assessment to Results in 8 Weeks
          </h2>
        </FadeInUp>
        
        <div className="max-w-4xl mx-auto">
          {/* Timeline Navigation */}
          <div className="flex justify-between mb-12 relative">
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-white/20 -translate-y-1/2" />
            {steps.map((step, index) => (
              <motion.button
                key={index}
                onClick={() => setActiveStep(index)}
                className="relative z-10"
                whileHover={{ scale: 1.1 }}
              >
                <motion.div
                  className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold transition-all ${
                    activeStep === index
                      ? 'bg-[#2563EB] text-white scale-110'
                      : activeStep > index
                      ? 'bg-green-500 text-white'
                      : 'bg-white/20 text-white/60'
                  }`}
                  animate={activeStep === index ? { scale: [1, 1.1, 1] } : {}}
                  transition={{ duration: 0.5, repeat: activeStep === index ? Infinity : 0, repeatDelay: 1 }}
                >
                  {index + 1}
                </motion.div>
                <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <span className={`text-xs ${activeStep === index ? 'text-[#2563EB]' : 'text-white/50'}`}>
                    {step.week}
                  </span>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 mt-16"
            >
              <h3 className="font-outfit text-2xl font-bold text-white mb-2">{steps[activeStep].title}</h3>
              <p className="text-white/70 mb-6">{steps[activeStep].description}</p>
              <div className="grid grid-cols-2 gap-4">
                {steps[activeStep].details.map((detail, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-2"
                  >
                    <CheckCircle2 className="w-4 h-4 text-[#2563EB]" />
                    <span className="text-white/80 text-sm">{detail}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

// Industries with Icons
const IndustriesSection = () => (
  <section className="section-padding" data-testid="industries-section">
    <div className="container-custom">
      <FadeInUp className="text-center mb-12">
        <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">Industries We Serve</span>
        <h2 className="font-outfit text-3xl md:text-4xl font-bold text-[#0A2540] mt-4">
          AI Solutions for Canadian Industries
        </h2>
      </FadeInUp>
      
      <StaggerContainer className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" staggerDelay={0.08}>
        {[
          { icon: ShoppingCart, name: "Retail" },
          { icon: Factory, name: "Manufacturing" },
          { icon: Stethoscope, name: "Healthcare" },
          { icon: Building2, name: "Professional Services" },
          { icon: Truck, name: "Logistics" },
          { icon: Users, name: "Hospitality" }
        ].map((industry, index) => (
          <StaggerItem key={index}>
            <motion.div 
              className="text-center p-6 bg-slate-50 rounded-2xl hover:bg-[#2563EB]/5 transition-colors group cursor-pointer"
              whileHover={{ y: -5, scale: 1.02 }}
              data-testid={`industry-${index}`}
            >
              <motion.div whileHover={{ rotate: 10, scale: 1.1 }}>
                <industry.icon className="w-12 h-12 text-[#2563EB] mx-auto mb-4" />
              </motion.div>
              <span className="text-sm font-medium text-[#0A2540]">{industry.name}</span>
            </motion.div>
          </StaggerItem>
        ))}
      </StaggerContainer>
    </div>
  </section>
);

// Interactive Pricing Section — REMOVED per client request
// (Component previously defined here has been removed.)

// FAQ Section
const FAQSection = () => (
  <section className="section-padding" data-testid="faq-section">
    <div className="container-custom">
      <FadeInUp className="text-center mb-12">
        <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">FAQ</span>
        <h2 className="font-outfit text-3xl md:text-4xl font-bold text-[#0A2540] mt-4">
          Common Questions About AI for SMBs
        </h2>
      </FadeInUp>
      
      <FadeInUp delay={0.2} className="max-w-3xl mx-auto">
        <Accordion type="single" collapsible className="space-y-4">
          {[
            { question: "What are AI solutions for small businesses in Canada?", answer: "AI solutions include chatbots, predictive analytics, automation tools, and intelligent systems that help make better business decisions. We specialize in making enterprise-grade technologies accessible and affordable for Canadian SMBs." },
            { question: "How much does AI automation cost for a small business?", answer: "Pricing depends on scope and where AI actually fits your business. We start every engagement with an AI readiness assessment, then scope a plan that fits your budget and priorities. Most clients see positive ROI within 3 to 6 months." },
            { question: "Is AI worth it for SMBs?", answer: "Absolutely. Our clients typically see 40-60% reduction in repetitive tasks, 35% improvement in response times, and average ROI of 340%." },
            { question: "How long does it take to implement AI solutions?", answer: "Most implementations take 4-8 weeks. We use proven frameworks and pre-built components that accelerate deployment while customizing for your needs." },
            { question: "Do I need technical expertise to use AI solutions?", answer: "No technical expertise required. We handle all complexity and provide user-friendly interfaces with comprehensive training." },
            { question: "How do you ensure data security with AI?", answer: "We follow enterprise-grade security including encryption, secure Canadian hosting, PIPEDA compliance, and regular audits. Your data is never shared." }
          ].map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="bg-white border border-slate-100 rounded-2xl px-6 data-[state=open]:shadow-lg transition-shadow"
              data-testid={`faq-item-${index}`}
            >
              <AccordionTrigger className="text-left font-medium text-[#0A2540] hover:no-underline py-6">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 pb-6">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </FadeInUp>
    </div>
  </section>
);

// Trust Badges
const TrustBadges = () => (
  <section className="py-12 bg-slate-50" data-testid="trust-badges">
    <div className="container-custom">
      <p className="text-center text-slate-500 text-sm mb-8">Built with industry-leading technologies</p>
      <StaggerContainer className="flex flex-wrap justify-center items-center gap-12" staggerDelay={0.1}>
        {['Microsoft Partner', 'Google Cloud', 'AWS Partner', 'OpenAI', 'MongoDB'].map((badge, index) => (
          <StaggerItem key={index}>
            <motion.div 
              className="flex items-center gap-2 text-slate-400 hover:text-slate-600 transition-colors"
              whileHover={{ scale: 1.1 }}
            >
              <Award className="w-5 h-5" />
              <span className="font-medium">{badge}</span>
            </motion.div>
          </StaggerItem>
        ))}
      </StaggerContainer>
    </div>
  </section>
);

// Final CTA
const FinalCTA = () => (
  <section className="section-padding bg-[#0A2540] relative overflow-hidden" data-testid="final-cta">
    <AnimatedBlobs />
    <div className="container-custom text-center relative z-10">
      <FadeInUp>
        <motion.div 
          className="inline-flex items-center gap-2 bg-[#2563EB]/20 border border-[#2563EB]/30 rounded-full px-4 py-2 mb-8"
          animate={{ scale: [1, 1.02, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <Clock className="w-4 h-4 text-[#2563EB]" />
          <span className="text-sm text-white/90">Start small. Prove value. Then scale.</span>
        </motion.div>
      </FadeInUp>
      
      <FadeInUp delay={0.2}>
        <h2 className="font-outfit text-3xl md:text-5xl font-bold text-white mb-6">
          Don&rsquo;t Let Competitors <br />Get Ahead with AI
        </h2>
      </FadeInUp>
      
      <FadeInUp delay={0.4}>
        <p className="text-white/70 text-lg mb-10 max-w-2xl mx-auto">
          Every day without AI optimization is money left on the table.
        </p>
      </FadeInUp>
      
      <FadeInUp delay={0.6}>
        <Link to="/contact">
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
            <Button 
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-10 py-6 text-lg font-medium shadow-lg shadow-[#2563EB]/25"
              data-testid="final-cta-button"
            >
              Get Free AI Assessment
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </Link>
      </FadeInUp>
      
      <FadeInUp delay={0.8}>
        <p className="text-white/50 text-sm mt-6">No commitment required. Takes only 15 minutes.</p>
      </FadeInUp>
    </div>
  </section>
);

export const AISolutionsPage = () => {
  return (
    <div data-testid="ai-solutions-page">
      <HeroSection />
      <StatsBar />
      {/* NEW: AI Readiness perspective, "Start with strategy, not software" */}
      <AIReadinessSection />
      <BeforeAfterSection />
      <SolutionsGrid />
      <ProcessTimeline />
      <WaveDivider color="#F8FAFC" flip />
      <IndustriesSection />
      <AISolutionsIndustrySection />
      <FAQSection />
      <TrustBadges />
      <FinalCTA />
    </div>
  );
};

export default AISolutionsPage;
